import '@banno/jha-wc/src/jha-nav/jha-nav';
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import '@jack-henry/jh-ui/components/button/button.js';
import { NavigationService } from '@treasury/core/navigation';
import { TmBaseComponent } from '@treasury/presentation';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { TmBoMenuItem, TmBoMenuItemChild, TmBoMenuItemParent } from './side-nav.types';

export const sideNavTagName = 'tm-side-nav';
@customElement(sideNavTagName)
export class TmBoSideNav extends TmBaseComponent {
    @property({
        type: String,
    })
    public title: string = '';

    @property({ type: Array })
    public menuItems: TmBoMenuItem[] | TmBoMenuItemParent[] = [];

    @InjectProperty()
    declare private navService: NavigationService;

    @property({ type: Boolean })
    private selected?: TmBoMenuItem | TmBoMenuItemChild | null;

    @property({ type: Boolean })
    private loading = false;

    @state()
    private childMenusExpanded?: boolean = false;

    public render() {
        if (this.loading) {
            return this.renderLoading();
        }

        return html`<div class="jha-nav-container pr-8">
            <jha-nav class="flex-none pt-0" .collapsible="${false}" .title=${this.title}>
                ${this.renderMenuItems()}</jha-nav
            >
        </div>`;
    }

    private handleMenuItemClick(e: Event, item: TmBoMenuItem | TmBoMenuItemChild) {
        e.preventDefault();
        this.selected = item;
        this.dispatchEvent(new CustomEvent('selected', { detail: item }));
        if (item.path) {
            this.navService.navigate(item.path);
        }
    }

    private renderMenuItem(item: TmBoMenuItem | TmBoMenuItemParent) {
        if ('children' in item && item.children.length > 0) {
            return html`<a
                    ?active=${item.children.some((childItem: TmBoMenuItemChild) => {
                        return this.selected?.path === childItem.path;
                    })}
                    href="javascript:void(0)"
                    class="menu-item whitespace-nowrap fill-current flex items-center"
                    @click=${(e: Event) => {
                        e.preventDefault();
                        this.childMenusExpanded = !this.childMenusExpanded;
                    }}
                >
                    ${item.icon} ${item.title}
                    <jha-icon-chevron-down
                        class="icon ${this.childMenusExpanded ? 'rotated' : ''}"
                    ></jha-icon-chevron-down>
                </a>
                ${this.childMenusExpanded
                    ? html`${item.children?.map(
                          (child: TmBoMenuItemChild) =>
                              html`<a
                                  ?active=${this.selected?.path === child.path}
                                  class="submenu-item whitespace-nowrap fill-current "
                                  href=${child.path}
                                  @click=${(e: Event) => this.handleMenuItemClick(e, child)}
                              >
                                  <span
                                      style="width: 32px !important; margin: var(--jha-icon-padding, 0); "
                                  ></span>
                                  ${child.title}
                              </a>`
                      )}`
                    : nothing} `;
        } else if ('path' in item) {
            return html`<a
                ?active=${this.selected?.path === item.path}
                class="menu-item whitespace-nowrap fill-current"
                @click=${(e: Event) => this.handleMenuItemClick(e, item)}
                href=${item.path}
            >
                ${item.icon} ${item.title}</a
            >`;
        } else {
            return html`<a
                ?active=${this.selected?.title === item.title}
                class="menu-item whitespace-nowrap fill-current"
                @click=${(e: Event) => this.handleMenuItemClick(e, item)}
            >
                ${item.icon} ${item.title}</a
            >`;
        }
    }

    private renderMenuItems() {
        return html`${this.menuItems?.map(item => this.renderMenuItem(item))}`;
    }

    private renderLoading() {
        const renderLoadingItem = () => {
            return html`<div class="flex w-48">
                <div class="bg-gray-300 mb-8 h-6 flex-none w-6 mr-4 rounded-sm animate-pulse"></div>
                <div class="bg-gray-300 mb-8 h-6 flex-1 rounded-sm animate-pulse"></div>
            </div>`;
        };

        return html`<div class="jha-nav-container py-2 px-8">
            ${Array.from({ length: 5 }).map(() => renderLoadingItem())}
        </div>`;
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public static styles = [
        css`
            :host {
                --tm-jh-menu-item-active-color: var(--jh-icon-fill-color, #085ce5);
                background-color: var(--jh-color-container-primary);
                height: calc(100vh - 65px);
            }
            .jha-nav-container {
                position: sticky !important;
                top: 0;
                z-index: 10;
            }

            .icon {
                transition: transform 0.3s ease;
            }

            .rotated {
                transform: rotate(180deg);
            }
            [active],
            a:hover {
                color: var(--tm-jh-menu-item-active-color) !important;
            }
        `,
    ];
}
